import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../../../context/FirebaseContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useAdminAuthTranslation } from './translations';
import LanguageSwitcher from '../../../components/layout/navbar/LanguageSwitcher/LanguageSwitcher';
import './AdminLoginPage.css';

const AdminLoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState('');
    const [resetError, setResetError] = useState('');

    // Get current language and translation
    const { language } = useLanguage();
    const { t } = useAdminAuthTranslation(language);

    const {
        currentUser,
        storeInfo,
        isAuthenticated,
        login,
        loginWithGoogle,
        resetPassword
    } = useFirebase();

    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        if (currentUser && storeInfo && isAuthenticated()) {
            navigate('/admin/dashboard', { replace: true });
        }
    }, [currentUser, storeInfo, isAuthenticated, navigate]);

    const handleGoogleSignIn = async () => {
        if (loading) return;

        setLoading(true);
        setError('');

        try {
            const result = await loginWithGoogle();

            if (result.success) {
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        setLoading(true);
        setError('');

        try {
            // Basic validation
            if (!email.trim() || !password.trim()) {
                throw new Error(t('pleaseEnterBothEmailAndPassword'));
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                throw new Error(t('pleaseEnterValidEmail'));
            }

            // Attempt login
            const result = await login(email.trim(), password);

            if (result.success) {
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (resetLoading) return;

        setResetLoading(true);
        setResetError('');
        setResetSuccess('');

        try {
            // Basic validation
            if (!resetEmail.trim()) {
                throw new Error(t('emailIsRequired'));
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(resetEmail.trim())) {
                throw new Error(t('pleaseEnterValidEmail'));
            }

            // Send reset email
            const result = await resetPassword(resetEmail.trim());

            if (result.success) {
                setResetSuccess(result.message);
                setResetEmail('');
                // Auto close the modal after 3 seconds
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setResetSuccess('');
                }, 3000);
            }
        } catch (err) {
            setResetError(err.message);
        } finally {
            setResetLoading(false);
        }
    };

    const handleCloseForgotPassword = () => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess('');
    };

    return (
        <div className="admin-login-container">
            {/* Language Switcher */}
            <div className="auth-language-switcher">
                <LanguageSwitcher />
            </div>

            <div className="admin-login-card">
                <div className="admin-login-header">
                    <h1>{t('adminLogin')}</h1>
                    <p>{t('loginToYourStore')}</p>
                </div>

                {error && (
                    <div className="admin-login-error">
                        {error}
                    </div>
                )}

                <form className="admin-login-form" onSubmit={handleSubmit}>
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className={`admin-login-button google-signin ${loading ? 'loading' : ''}`}
                    >
                        {loading ? t('signingIn') : (
                            <>
                                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                {t('continueWithGoogle')}
                            </>
                        )}
                    </button>

                    <div className="auth-divider">
                        <span>{t('or')}</span>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{t('emailAddress')}</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            required
                            placeholder={t('enterYourEmail')}
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">{t('password')}</label>
                        <div className="password-input-container">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                required
                                placeholder={t('enterYourPassword')}
                                autoComplete="current-password"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`admin-login-button ${loading ? 'loading' : ''}`}
                    >
                        {loading ? t('signingIn') : t('signIn')}
                    </button>

                    <div className="forgot-password-link">
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="link-button"
                            disabled={loading}
                        >
                            {t('forgotPassword')}
                        </button>
                    </div>
                </form>


                <div className="admin-login-footer">
                    <p>{t('dontHaveAccount')} <a href="/admin/register">{t('createStore')}</a></p>

                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{t('resetPassword')}</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={handleCloseForgotPassword}
                                disabled={resetLoading}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <p>{t('enterEmailForReset')}</p>

                            {resetError && (
                                <div className="admin-login-error">
                                    {resetError}
                                </div>
                            )}

                            {resetSuccess && (
                                <div className="admin-login-success">
                                    {resetSuccess}
                                </div>
                            )}

                            <form onSubmit={handleForgotPassword}>
                                <div className="form-group">
                                    <label htmlFor="resetEmail">{t('emailAddress')}</label>
                                    <input
                                        id="resetEmail"
                                        type="email"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        disabled={resetLoading}
                                        required
                                        placeholder={t('enterYourEmail')}
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        onClick={handleCloseForgotPassword}
                                        disabled={resetLoading}
                                        className="admin-login-button secondary"
                                    >
                                        {t('backToSignIn')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className={`admin-login-button ${resetLoading ? 'loading' : ''}`}
                                    >
                                        {resetLoading ? t('sending') : t('sendResetLink')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLoginPage;